import { useTranslation } from 'react-i18next';
import { formatAmount, RollingReserve, Skeleton, withStyles } from 'vopay';
import { BodyText, Card, Title, TruncatedText } from 'UI';
import useTransactionLabels from '@/components/Entities/hooks/useTransactionLabels';
import styles from './AvailableBalanceCard.module.scss';

const cn = withStyles(styles);

type AvailableBalanceCardProps = {
  currency: string;
  availableBalance: string;
  pendingBalance: string;
  reserve: string;
  /** The account's rolling-reserve rows; undefined while loading, null when they could not be loaded. */
  rollingReserves: RollingReserve[] | null | undefined;
  /**
   * The account page's 363px card (Figma 557:85706): the tiles show the amount alone, the hero having
   * already named the currency. The drawer's 567px card spells it out on every tile (Figma 65:14741).
   */
  compact?: boolean;
};

const NOT_AVAILABLE = '—';

/** One rendered policy: a formatted rate, and the rail it is scoped to (empty when it covers all). */
type PolicyLine = { percentage: string; transactionType: string };

/**
 * The balance card (spec 5.5): available amount as the hero figure, with Pending, Reserve and the
 * reserve policy underneath. Shared by the details drawer and the full account page.
 */
export default function AvailableBalanceCard({
  currency,
  availableBalance,
  pendingBalance,
  reserve,
  rollingReserves,
  compact = false,
}: AvailableBalanceCardProps) {
  const { t, i18n } = useTranslation('translations', {
    keyPrefix: 'Accounts.AccountHeaderCards.AvailableBalanceCard',
  });

  const { resolveTypeLabel } = useTransactionLabels();

  const formatBalance = (amount: string) => (compact ? formatAmount(amount) : `${currency} ${formatAmount(amount)}`);

  /**
   * The rate as it is set, not as it is stored: the column is DECIMAL(5,2), so a 20% reserve arrives
   * as "20.00" and the design shows "20%". Trailing zeros go, real decimals stay — 2.50 reads 2.5.
   * A value that will not parse is shown as it came, rather than as NaN.
   */
  const formatPercentage = (value: string) => {
    const rate = parseFloat(value);

    return Number.isFinite(rate)
      ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(rate)
      : value;
  };

  const policy = rollingReserves ? toPolicyLines(rollingReserves, formatPercentage) : rollingReserves;

  const policyLabel = ({ percentage, transactionType }: PolicyLine) =>
    transactionType
      ? t('rollingPolicyForType', {
          defaultValue: '{{percentage}}% rolling · {{transactionType}}',
          percentage,
          transactionType: resolveTypeLabel(transactionType),
        })
      : t('rollingPolicy', { defaultValue: '{{percentage}}% rolling', percentage });

  // One rate per line rather than a comma-joined sentence, which wraps mid-word and doubles the
  // card's height as soon as an account carries two policies.
  const policyContent = () => {
    if (!policy) return NOT_AVAILABLE;
    if (policy.length === 0) return t('noPolicy', { defaultValue: 'None' });

    return policy.map((line) => (
      <span key={`${line.percentage}|${line.transactionType}`} className={cn('policyLine')}>
        {policyLabel(line)}
      </span>
    ));
  };

  /**
   * The tile shows the first policy and a count of the rest, the full list on hover — on the page and
   * in the drawer alike. Listing them outright in the drawer's wider tile still wrapped a rail-scoped
   * policy onto two lines and grew the card past its two neighbours.
   */
  const policyTile = () => {
    if (!policy || policy.length === 0) {
      return (
        <BodyText className={cn('tileValue')} data-testid="reservePolicy">
          {policyContent()}
        </BodyText>
      );
    }

    const [first, ...rest] = policy;

    return (
      <BodyText className={cn('tileValue')} data-testid="reservePolicy">
        <TruncatedText information={policyContent()}>
          {policyLabel(first)}
          {rest.length > 0 && ` ${t('morePolicies', { defaultValue: '+{{remaining}}', remaining: rest.length })}`}
        </TruncatedText>
      </BodyText>
    );
  };

  return (
    <Card variant="section" className={cn('card', compact && 'compact')} data-testid="availableBalanceCard">
      <div className={cn('hero')}>
        <Title level="6" className={cn('cardTitle')}>
          {t('title', { defaultValue: 'Available Balance' })}
        </Title>

        {/* Figma: H6/Title 15/Emphasized currency beside an H3/24 bold amount — a figure, not a heading */}
        <div className={cn('heroAmount')} data-testid="availableBalance">
          <span className={cn('heroCurrency')}>{currency}</span>{' '}
          <span className={cn('heroValue')}>{formatAmount(availableBalance)}</span>
        </div>
      </div>

      <div className={cn('tiles')}>
        <div className={cn('tile')}>
          <span className={cn('tileLabel')}>{t('pending', { defaultValue: 'Pending' })}</span>

          <BodyText className={cn('tileValue')} data-testid="pendingBalance">
            <TruncatedText>{formatBalance(pendingBalance)}</TruncatedText>
          </BodyText>
        </div>

        <div className={cn('tile')}>
          <span className={cn('tileLabel')}>{t('reserve', { defaultValue: 'Reserve' })}</span>

          <BodyText className={cn('tileValue')} data-testid="reserveBalance">
            <TruncatedText>{formatBalance(reserve)}</TruncatedText>
          </BodyText>
        </div>

        <div className={cn('tile')}>
          <span className={cn('tileLabel')}>{t('policy', { defaultValue: 'Policy' })}</span>
          {policy === undefined ? <Skeleton width={80} height={24} /> : policyTile()}
        </div>
      </div>
    </Card>
  );
}

/**
 * One line per distinct rate-and-rail pair, deduplicated after formatting so rows differing only in
 * stored precision collapse. Global rows sort first: a row with no transaction type applies to every
 * rail its reserve type covers, so it reads as the headline policy and the rail-specific rows as the
 * exceptions to it.
 */
function toPolicyLines(rows: RollingReserve[], formatPercentage: (value: string) => string) {
  const byRateAndRail = new Map<string, PolicyLine>();

  rows.forEach((row) => {
    const percentage = formatPercentage(row.ReservePercentage);

    byRateAndRail.set(`${percentage}|${row.TransactionType}`, { percentage, transactionType: row.TransactionType });
  });

  return [...byRateAndRail.values()].sort(
    (first, second) => Number(Boolean(first.transactionType)) - Number(Boolean(second.transactionType)),
  );
}
